// src/encounter/lib/lab-bill-to.ts
// BILL TO for a lab order (PROD-2640, canvas artboards 6-8: "Bill to" Patient / Clinic).
//
// Wire: `billTo` on each A&P lab order (Scrips.Patient #1000, P-B6): "Patient" | "Clinic" | null,
// stored on the order and the lab request, and returned on the A&P read (the reopen). The backend
// never invents a payer: the default below is the popup's job.

export type LabBillToValue = 'Patient' | 'Clinic';

/**
 * Every vendor defaults to Clinic (Andrew's ruling): the patient pays the clinic, and the clinic
 * pays the vendor. The vendor's own billingResponsibility is deliberately not read.
 */
export const LAB_BILL_TO_DEFAULT: LabBillToValue = 'Clinic';

/** The design's hint under BILL TO: "Default for NRL: Clinic", or the override sentence. */
export function billToHint(vendorName: string | undefined, value: LabBillToValue): { lead: string; clinic?: string } {
  const who = vendorName?.trim();
  if (value === LAB_BILL_TO_DEFAULT) {
    return { lead: who ? `Default for ${who}: ${LAB_BILL_TO_DEFAULT}` : `Default: ${LAB_BILL_TO_DEFAULT}` };
  }
  return {
    lead: who ? `Overrides ${who}'s default (${LAB_BILL_TO_DEFAULT}).` : `Overrides the default (${LAB_BILL_TO_DEFAULT}).`,
    ...(value === 'Clinic' ? { clinic: 'The clinic pays the lab and bills the patient.' } : {}),
  };
}

/**
 * The A&P read's `billTo` → the control's value. Patient stores canonical casing, but any casing is
 * accepted here too; anything else (null, blank, unknown) is "not saved" → undefined, and the popup
 * then shows the default.
 */
export function readBillTo(v: unknown): LabBillToValue | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim().toLowerCase();
  if (t === 'patient') return 'Patient';
  if (t === 'clinic') return 'Clinic';
  return undefined;
}
